import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { AppUser, UserPermissions } from '@/shared/types/user';

interface UserContextValue {
  user: AppUser | null;
  permissions: UserPermissions | null;
  setUser: (user: AppUser | null, permissions?: UserPermissions | null) => void;
}

// Context는 내부에서만 사용 (Fast Refresh 경고 방지)
const UserContext = createContext<UserContextValue | null>(null);

/**
 * 사용자 정보 접근 훅
 * @throws UserProvider 외부에서 사용 시 에러 발생
 */
export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser는 UserProvider 내부에서만 사용할 수 있습니다.');
  }
  return ctx;
}

/**
 * 사용자 정보 접근 훅 (선택적)
 * UserProvider 외부에서 호출해도 null 반환
 */
export function useOptionalUser() {
  return useContext(UserContext);
}

/**
 * 사용자 정보 제공 컴포넌트
 * 애플리케이션 최상위에서 사용
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AppUser | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);

  const setUser = (newUser: AppUser | null, newPermissions?: UserPermissions | null) => {
    setUserState(newUser);
    if (newPermissions !== undefined) {
      setPermissions(newPermissions);
    }
  };

  const value = useMemo(() => ({ user, permissions, setUser }), [user, permissions]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
